import { useAuth0 } from '@auth0/auth0-react'
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  useDisclosure,
} from '@chakra-ui/react'
import type React from 'react'
import { useReducer, useState } from 'react'
import { BiNetworkChart } from 'react-icons/bi'
import { HiOutlineCurrencyEuro } from 'react-icons/hi'
import { MdOutlineClose } from 'react-icons/md'
import {
  RiArrowDownSLine,
  RiBankLine,
  RiMenu2Line,
  RiPriceTag3Line,
} from 'react-icons/ri'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import AnalyticsUpIcon from '../../../assets/icons/analytics-up-stroke-rounded (1)'
import ArrowDataTransferHorizontalIcon from '../../../assets/icons/arrow-data-transfer-horizontal-stroke-rounded (1)'
import BankIcon from '../../../assets/icons/bank-stroke-rounded'
import DashboardSpeed02Icon from '../../../assets/icons/dashboard-speed-02-stroke-rounded'
import Logout02Icon from '../../../assets/icons/logout-02-stroke-rounded (1)'
import UserGroupIcon from '../../../assets/icons/user-group-stroke-rounded (2)'
import Wallet03Icon from '../../../assets/icons/wallet-03-stroke-rounded'
import ZapIcon from '../../../assets/icons/zap-stroke-rounded'
import penningSymbol from '../../../assets/symbol.png'
import thorChainLogo from '../../../assets/thorchain-logo.png'
import UserIcon from '../../../assets/user-stroke-rounded'
import AnimateHeight from '../../../components/AnimateHeight'
import MenuItem from '../../../components/MenuItem'
import WelcomeModal from '../../../components/WelcomeModal'
import { AUTH0_LOGOUT_URL, INVEST_DEPOSITS_DISABLED } from '../../../constants'
import { clearImpersonationSnapshot } from '../../../redux/store'
import type { RootState } from '../../../redux/store'
import {
  isAdmin,
  isBookkeeper,
  isPartner,
  isWealthManager,
} from '../../../utils/roleUtils'
import NeedHelp from './NeedHelp'

type Props = {}

const AppMenu: React.FC<Props> = () => {
  const history = useHistory()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [, forceUpdate] = useReducer(x => x + 1, 0)
  const welcomeModal = useDisclosure()

  const { logout } = useAuth0()

  const sessionUser = useSelector((state: RootState) => state.session?.user)

  // Clients who transferred in from Veli (ADR-0004) don't yet have their
  // investment portfolio replicated on Penning, so the Investing surfaces would
  // only show an empty/confusing "portfolio". Grey them out as "coming soon"
  // until their products are migrated (the route guards in AppNavigator keep the
  // screens unreachable to match). Server-authoritative, so it survives re-login.
  const isVeliMigrating = sessionUser?.isVeliOrigin ?? false

  type Page = {
    title: string
    path: string
    icon: JSX.Element
    comingSoon?: boolean
    isNewFeature?: boolean
    links?: { title: string; path: string }[]
    externalUrl?: string
  }

  type PageSection = {
    id: string
    title?: string
    pages: Page[]
  }

  const individualSections: PageSection[] = [
    {
      id: 'onboarding',
      title: 'Onboarding',
      pages: [
        {
          title: 'Get started',
          path: '/app/get-started',
          icon: <ZapIcon className="w-5 h-5 text-inherit" />,
        },
      ],
    },
    {
      id: 'trading',
      title: 'Trading',
      pages: [
        {
          title: 'Broker Desk',
          path: '/app/broker-service',
          icon: (
            <ArrowDataTransferHorizontalIcon className="w-5 h-5 text-inherit" />
          ),
        },
        {
          title: 'Native Swap',
          path: '/app/thorchain-swap',
          icon: (
            <div className="flex items-center justify-center w-5 h-5">
              <img src={thorChainLogo} alt="Native swap" className="h-4.5" />
            </div>
          ),
        },
      ],
    },
    {
      id: 'investing',
      title: 'Investing',
      pages: [
        {
          title: 'Portfolio Performance',
          path: '/app/investor',
          icon: <AnalyticsUpIcon className="w-5 h-5 text-inherit" />,
          comingSoon: isVeliMigrating,
        },
        {
          title: 'Portfolio Deposits',
          path: '/app/fund-deposits',
          icon: <RiBankLine className="w-5 h-5 text-inherit" />,
          comingSoon: INVEST_DEPOSITS_DISABLED || isVeliMigrating,
        },
      ],
    },
    {
      id: 'account',
      title: 'Account',
      pages: [
        {
          title: 'Wallets',
          path: '/app/wallets',
          icon: <Wallet03Icon className="w-5 h-5 text-inherit" />,
        },
        {
          title: 'Bank Accounts',
          path: '/app/bank-accounts',
          icon: <BankIcon className="w-5 h-5 text-inherit" />,
        },
        {
          title: 'Account',
          path: '/app/account',
          icon: <UserIcon className="w-5 h-5 text-inherit" />,
        },
      ],
    },
  ]

  const adminPages: Page[] = [
    {
      title: 'Clients',
      path: '/app/admin/clients',
      icon: <UserGroupIcon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Transfers',
      path: '/app/admin/transfers',
      icon: (
        <ArrowDataTransferHorizontalIcon className="w-5 h-5 text-inherit" />
      ),
    },
    {
      title: 'Currencies',
      path: '/app/admin/currencies',
      icon: <HiOutlineCurrencyEuro className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Wallets',
      path: '/app/admin/system-wallets',
      icon: <Wallet03Icon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Networks',
      path: '/app/admin/networks',
      icon: <BiNetworkChart className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Bank Accounts',
      path: '/app/admin/bank-accounts',
      icon: <RiBankLine className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'OTC Performance',
      path: '/app/admin/performance',
      icon: <DashboardSpeed02Icon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Fund performance',
      path: '/app/admin/fund',
      icon: <AnalyticsUpIcon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Customer Insights',
      path: '/app/admin/customer-insights',
      icon: <DashboardSpeed02Icon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'CRM',
      path: '/app/admin/crm',
      icon: <DashboardSpeed02Icon className="w-5 h-5 text-inherit" />,
      externalUrl: 'https://crm.penning.io/dashboard',
    },
    {
      title: 'Fee Structures',
      path: '/app/admin/fee-structures',
      icon: <RiPriceTag3Line className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Affiliate Partners',
      path: '/app/admin/affiliate-partners',
      icon: <UserGroupIcon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Bookkeeping',
      path: '/app/admin/bookkeeping',
      icon: <BankIcon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Finanstilsynet',
      path: '/app/admin/compliance/finanstilsynet',
      icon: <BankIcon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Rule sets',
      path: '/app/admin/compliance/rule-sets',
      icon: <BankIcon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Account',
      path: '/app/account',
      icon: <UserIcon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Veli Migration',
      path: '/app/admin/veli-migration',
      icon: <UserGroupIcon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Team',
      path: '/app/admin/team',
      icon: <UserGroupIcon className="w-5 h-5 text-inherit" />,
    },
  ]

  const adminSections: PageSection[] = [
    {
      id: 'operations',
      title: 'Operations',
      pages: [
        adminPages[0],
        adminPages[1],
        adminPages[12],
        adminPages[13],
        adminPages[14],
      ],
    },
    {
      id: 'treasury',
      title: 'Treasury',
      pages: [
        adminPages[2],
        adminPages[3],
        adminPages[4],
        adminPages[5],
        adminPages[10],
      ],
    },
    {
      id: 'insights',
      title: 'Insights',
      pages: [adminPages[6], adminPages[7], adminPages[8]],
    },
    {
      id: 'growth',
      title: 'Growth',
      pages: [adminPages[9], adminPages[11], adminPages[16]],
    },
    {
      id: 'account',
      title: 'Account',
      // Team (staff invites) is Admin-only — the invite endpoint is gated on Team_Invite, which only
      // the full Admin role holds, so don't surface it to FinanceOfficer / OperationsOfficer.
      pages:
        sessionUser.role === 'Admin'
          ? [adminPages[17], adminPages[15]]
          : [adminPages[15]],
    },
  ]

  const wealthManagerSections: PageSection[] = [
    {
      id: 'overview',
      title: 'Overview',
      pages: [
        {
          title: 'Dashboard',
          path: '/app/wealth-manager',
          icon: <DashboardSpeed02Icon className="w-5 h-5 text-inherit" />,
        },
      ],
    },
    {
      id: 'account',
      title: 'Account',
      pages: [
        {
          title: 'Account',
          path: '/app/account',
          icon: <UserIcon className="w-5 h-5 text-inherit" />,
        },
      ],
    },
  ]

  const partnerPages: Page[] = [
    {
      title: 'Customer Insights',
      path: '/app/admin/customer-insights',
      icon: <DashboardSpeed02Icon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Performance',
      path: '/app/admin/performance',
      icon: <DashboardSpeed02Icon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Fund performance',
      path: '/app/admin/fund',
      icon: <AnalyticsUpIcon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Affiliate Partners',
      path: '/app/admin/affiliate-partners',
      icon: <UserGroupIcon className="w-5 h-5 text-inherit" />,
    },
  ]

  const partnerSections: PageSection[] = [
    {
      id: 'insights',
      title: 'Insights',
      pages: [partnerPages[0], partnerPages[1], partnerPages[2]],
    },
    {
      id: 'partners',
      title: 'Partners',
      pages: [partnerPages[3]],
    },
  ]

  const complianceOfficerPages: Page[] = [
    {
      title: 'Clients',
      path: '/app/admin/clients',
      icon: <UserGroupIcon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Transfers',
      path: '/app/admin/transfers',
      icon: (
        <ArrowDataTransferHorizontalIcon className="w-5 h-5 text-inherit" />
      ),
    },
    {
      title: 'Finanstilsynet',
      path: '/app/admin/compliance/finanstilsynet',
      icon: <BankIcon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Rule sets',
      path: '/app/admin/compliance/rule-sets',
      icon: <BankIcon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Veli Migration',
      path: '/app/admin/veli-migration',
      icon: <UserGroupIcon className="w-5 h-5 text-inherit" />,
    },
    {
      title: 'Account',
      path: '/app/account',
      icon: <UserIcon className="w-5 h-5 text-inherit" />,
    },
  ]

  const complianceOfficerSections: PageSection[] = [
    {
      id: 'operations',
      title: 'Operations',
      pages: [
        complianceOfficerPages[0],
        complianceOfficerPages[1],
        complianceOfficerPages[2],
        complianceOfficerPages[3],
        complianceOfficerPages[4],
      ],
    },
    {
      id: 'account',
      title: 'Account',
      pages: [complianceOfficerPages[5]],
    },
  ]

  const bookkeeperPages: Page[] = [
    {
      title: 'Swap Invoices',
      path: '/app/admin/transfers',
      icon: (
        <ArrowDataTransferHorizontalIcon className="w-5 h-5 text-inherit" />
      ),
    },
    {
      title: 'Portfolio Deposits',
      path: '/app/admin/fund-deposits',
      icon: (
        <ArrowDataTransferHorizontalIcon className="w-5 h-5 text-inherit" />
      ),
    },
    {
      title: 'Account',
      path: '/app/account',
      icon: <UserIcon className="w-5 h-5 text-inherit" />,
    },
  ]

  const bookkeeperSections: PageSection[] = [
    {
      id: 'operations',
      title: 'Operations',
      pages: [bookkeeperPages[0], bookkeeperPages[1]],
    },
    {
      id: 'account',
      title: 'Account',
      pages: [bookkeeperPages[2]],
    },
  ]

  const utilsItems = [
    {
      title: 'Log out',
      icon: <Logout02Icon className="w-5 h-5 text-inherit" />,
      onClick: () => {
        clearImpersonationSnapshot()
        logout({ returnTo: AUTH0_LOGOUT_URL })
      },
    },
  ]

  const sectionsToMap: PageSection[] = isWealthManager(sessionUser.role)
    ? wealthManagerSections
    : isPartner(sessionUser.role)
      ? partnerSections
      : isAdmin(sessionUser.role)
        ? sessionUser.role === 'ComplianceOfficer'
          ? complianceOfficerSections
          : isBookkeeper(sessionUser.role)
            ? bookkeeperSections
            : adminSections
        : individualSections

  const getStoredCollapsedSections = () => {
    const storageKey = `app-menu-collapsed-sections:${sessionUser.role}`
    const storedValue = localStorage.getItem(storageKey)

    if (!storedValue) {
      return {}
    }

    try {
      return JSON.parse(storedValue) as Record<string, boolean>
    } catch {
      return {}
    }
  }

  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >(getStoredCollapsedSections)

  const toggleSection = (sectionId: string) => {
    const storageKey = `app-menu-collapsed-sections:${sessionUser.role}`

    setCollapsedSections(prev => {
      const next = {
        ...prev,
        [sectionId]: !prev[sectionId],
      }

      localStorage.setItem(storageKey, JSON.stringify(next))

      return next
    })
  }

  const handleMenuItemClick = (item: Page, onAfterNavigate?: () => void) => {
    if (item.externalUrl) {
      window.open(item.externalUrl, '_blank', 'noopener,noreferrer')
    } else if (item.links) {
      history.push(item.links[0].path)
    } else {
      history.push(item.path)
    }

    onAfterNavigate?.()
  }

  const renderSection = (
    section: PageSection,
    index: number,
    onAfterNavigate?: () => void
  ) => {
    const isDynamicSidebar = isDynamicSidebarActivated()
    const isCollapsible = !!section.title && !isDynamicSidebar
    const isCollapsed = isCollapsible ? !!collapsedSections[section.id] : false

    return (
      <div
        key={`${section.id}-${index}`}
        className={index > 0 ? 'mt-4 pt-4 border-t border-black/10' : ''}
      >
        {section.title && !isDynamicSidebar && (
          <button
            type="button"
            onClick={() => toggleSection(section.id)}
            className="w-full mb-2 px-2.5 py-2 rounded-xl flex items-center justify-between gap-3 text-left transition-all hover:bg-black/[0.045]"
          >
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-black/45">
                {section.title}
              </p>
            </div>
            <div className="flex items-center shrink-0">
              <RiArrowDownSLine
                className={`w-4 h-4 text-black/45 transition-transform duration-200 ${
                  isCollapsed ? '-rotate-90' : 'rotate-0'
                }`}
              />
            </div>
          </button>
        )}
        <AnimateHeight durationInS={0.2} isVisible={!isCollapsed}>
          <div>
            {section.pages.map((item, itemIndex) => {
              const isActive = history.location.pathname.includes(item.path)

              return (
                <MenuItem
                  key={`${item.path}-${itemIndex}`}
                  icon={item.icon}
                  title={item.title}
                  onClick={() => handleMenuItemClick(item, onAfterNavigate)}
                  isNewFeature={item.isNewFeature}
                  isActive={isActive}
                  comingSoon={item.comingSoon}
                  links={item.links}
                  isExternal={!!item.externalUrl}
                />
              )
            })}
          </div>
        </AnimateHeight>
      </div>
    )
  }

  window.addEventListener('storage', () => {
    forceUpdate()
  })

  const isDynamicSidebarActivated = () => {
    return localStorage.getItem('dynamic-sidebar') === 'on'
  }

  return (
    <div
      className={`${
        isDynamicSidebarActivated()
          ? 'md:w-[55px] w-full '
          : 'w-full md:w-[250px]'
      } h-[60px] md:h-full w-full transition-all shrink-0 group bg-brand-app-menu flex md:flex-col p-2  border-r-[1px] border-solid border-brand-app-menu-hover/50 z-50`}
    >
      <WelcomeModal
        isOpen={welcomeModal.isOpen}
        onClose={welcomeModal.onClose}
      />

      <div className="hidden md:flex md:flex-col gap-1 justify-between h-full min-h-0">
        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          <div className="flex items-center gap-3 mb-10 p-1.5 overflow-hidden whitespace-nowrap">
            <div
              className="h-8 w-8 flex items-center justify-center shrink-0 bg-[#9F9F92] rounded-lg cursor-pointer"
              onClick={welcomeModal.onOpen}
            >
              <img src={penningSymbol} alt="Penning logo" className="h-5" />
            </div>
            {isDynamicSidebarActivated() ? <></> : <></>}
          </div>

          {sectionsToMap.map((section, index) => renderSection(section, index))}
        </div>
        <div className="shrink-0">
          <NeedHelp
            company={
              history.location.pathname.includes('investor') ? 'pcm' : 'otc'
            }
          />

          {utilsItems.map((item, i) => {
            return (
              <MenuItem
                key={i}
                icon={item.icon}
                title={item.title}
                onClick={item.onClick}
                isActive={false}
              />
            )
          })}
        </div>
      </div>
      <div className="md:hidden text-black flex items-center px-1 ">
        <div className="p-1" onClick={onOpen}>
          <RiMenu2Line color="#000" size={24} />
        </div>
        <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="full">
          <DrawerOverlay />
          <DrawerContent
            bg="#E5E5D3"
            h="100dvh"
            display="flex"
            flexDirection="column"
            overflowY="auto"
            sx={{
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
            }}
          >
            <div className="flex items-center justify-between px-5 pt-5">
              {/* <img className="w-[50px]" src={logo} alt="Penning logo" /> */}

              <div className="p-4 rounded-full" onClick={onClose}>
                <MdOutlineClose size={26} color="#000" />
              </div>
            </div>

            <DrawerBody
              mt="20px"
              flex="1"
              minH={0}
              overflow="visible"
              pb="32px"
            >
              <div className="flex flex-col gap-2">
                {sectionsToMap.map((section, index) =>
                  renderSection(section, index, onClose)
                )}
                <div className="mt-10">
                  <NeedHelp
                    company={
                      history.location.pathname.includes('investor')
                        ? 'pcm'
                        : 'otc'
                    }
                  />
                  {utilsItems.map((item, i) => {
                    return (
                      <MenuItem
                        key={i}
                        icon={item.icon}
                        title={item.title}
                        onClick={item.onClick}
                        isActive={false}
                      />
                    )
                  })}
                </div>
              </div>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </div>
    </div>
  )
}

export default AppMenu
